import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const steps = [
  {
    number: "01",
    icon: "ri-user-add-line",
    title: "Create Your Free Account",
    description:
      "Sign up in under 2 minutes. No credit card required to register — just your name, email, and company info. You'll get instant access to your installer dashboard.",
    details: [
      "Free to join, no setup fees",
      "Instant dashboard access",
      "Secure Stripe billing on file when you're ready to buy",
    ],
    color: "bg-green-50",
    iconColor: "text-green-600",
    borderColor: "border-green-200",
    numberColor: "text-green-500",
  },
  {
    number: "02",
    icon: "ri-settings-3-line",
    title: "Set Your Service States & Score Threshold",
    description:
      "Head to Settings and configure exactly which states you serve, your target ZIP codes, and the minimum lead score you'll accept (50–99). Only leads matching your criteria will appear in your marketplace.",
    details: [
      "Filter by state and ZIP code",
      "Set minimum Lead Score (50–99)",
      "Update preferences anytime",
    ],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    borderColor: "border-amber-200",
    numberColor: "text-amber-500",
  },
  {
    number: "03",
    icon: "ri-store-2-line",
    title: "Browse, Buy & Manage Your Leads",
    description:
      "Your live marketplace shows only leads that match your settings. Reserve a lead for 5 minutes, complete checkout via Stripe, and full contact details unlock instantly. Track every lead through your pipeline.",
    details: [
      "5-minute reservation holds your lead",
      "Full contact info unlocks after payment",
      "Pipeline: New → Contacted → Closed",
    ],
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    borderColor: "border-blue-200",
    numberColor: "text-blue-500",
  },
];

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="onboarding-flow" className="py-20 md:py-28 bg-gray-50/60">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wide">
            <i className="ri-map-2-line"></i>
            Getting Started
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
            What happens after you sign up
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            From account creation to your first purchased lead — here's exactly what to expect.
          </p>
        </div>

        {/* Steps */}
        <div ref={ref} className="relative">
          {/* Connector line — desktop only */}
          <div className="hidden lg:block absolute top-16 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5 bg-gradient-to-r from-green-200 via-amber-200 to-blue-200 z-0"></div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 relative z-10">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className={`transition-all duration-700 ${
                  visible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className={`bg-white rounded-2xl border ${step.borderColor} p-6 md:p-8 h-full flex flex-col`}>
                  {/* Step number + icon */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${step.color} flex-shrink-0`}>
                      <i className={`${step.icon} ${step.iconColor} text-2xl`}></i>
                    </div>
                    <span className={`text-4xl font-black ${step.numberColor} leading-none`}>
                      {step.number}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">
                    {step.description}
                  </p>

                  {/* Detail bullets */}
                  <ul className="flex flex-col gap-2">
                    {step.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className={`ri-check-line ${step.iconColor} text-sm font-bold`}></i>
                        </div>
                        <span className="text-gray-600 text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="/signup"
            onClick={(e) => { e.preventDefault(); navigate("/signup"); }}
            className="inline-flex items-center gap-2 bg-black text-white font-bold px-8 py-4 rounded-full text-base hover:bg-gray-900 transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap"
          >
            Get Started — It&apos;s Free
            <i className="ri-arrow-right-line text-lg"></i>
          </a>
          <p className="text-gray-400 text-sm mt-3">No credit card required to sign up</p>
        </div>
      </div>
    </section>
  );
}
